import type {CheckoutDocumentStatus, LinkedContractDocument} from "pay/checkoutDocument";
import {OLDEST_ACCEPTED_TERMS_VERSION, TERMS_VERSION_PATTERN} from "./TermsStorage";

/**
 * What an "I agree" click records, given what the api has announced so far (kitecorp/transferify-ui#3485).
 *
 * `wait` holds the click until the announcement answers; `record` names the version to store with it.
 */
export type AcceptanceVersion =
	| {readonly kind: "wait"}
	| {readonly kind: "record"; readonly version: string};

const WAIT: AcceptanceVersion = Object.freeze({kind: "wait"});

/**
 * The Terms version an "I agree" click accepts. Never a version typed into the ui: a hand-typed one went on
 * naming 2026-07-10 while the Terms on screen were dated 2026-10-09.
 *
 * FIRST CHOICE, KEPT ON PURPOSE: the registered version the api announces for this reader and language,
 * read from `GET /pay/contract-document`, the announcement the checkout's contract document comes from. A
 * record naming it names a document the api has registered and serves by that version
 * (`GET /pay/contract-documents/<version>/<locale>`), the same bytes a checkout attaches. Its cost: the ui
 * deploys before the api, so until the api's next deploy the api still announces the previous version and a
 * click records that one, not the one on screen. The api pull request merges only once the ui is on prod
 * (CLAUDE.md, "Linked pairs merge api first"), which keeps that window to about a day in prod; in the other
 * order a click would record a version the buyer was not shown (kitecorp/transferify-ui#3524,
 * kitecorp/transferify-ui#3848).
 *
 * FALLBACK, `published`: whenever the announcement gives no version that can be recorded, the click names
 * the version this bundle publishes, the Terms on screen (`./publishedTermsVersion`). That covers a lookup
 * that failed or timed out, an api that announces nothing, a version that is not a policy date (the only
 * shape the api's acceptance record takes), and one older than {@link OLDEST_ACCEPTED_TERMS_VERSION}, which
 * the gate would refuse to count. So every click names a version and the replay sends it: no account is left
 * without a server-side acceptance record because one lookup failed.
 *
 * The api is asked at the click, so the click waits for the answer, both before the lookup has started
 * (`disabled`) and while it is on its way (`pending`); the lookup carries its own timeout, so the wait always
 * ends.
 */
export function acceptanceVersion(status: CheckoutDocumentStatus, document: LinkedContractDocument | null,
	published: string): AcceptanceVersion {
	if (status === "disabled" || status === "pending") {
		return WAIT;
	}
	const announced = status === "ready" ? document?.identity.version ?? null : null;
	const recordable = announced !== null && TERMS_VERSION_PATTERN.test(announced)
		&& announced >= OLDEST_ACCEPTED_TERMS_VERSION;
	return {kind: "record", version: recordable ? announced : published};
}
